import type { KeyItem, MeterReading, PreviousKey, PreviousMeter } from './handovers'

/**
 * Taking the last protocol's readings and keys into the one being written.
 *
 * Pure functions in the shared package for the same reason as
 * `handover-parties.ts`: they decide what lands in a legal document, and the
 * web package has no test runner.
 *
 * Two ways in, with different rules:
 *
 *   - ONE row ("Übernehmen" beside it) is an explicit instruction and
 *     OVERWRITES what the row holds — somebody who taps it next to a value they
 *     typed wants the previous value instead.
 *   - ALL rows ("Alle übernehmen") fills BLANKS only. One tap must not be able
 *     to replace five readings that were just read off the meters.
 *
 * Everything taken is an ordinary field afterwards: editable, like anything
 * typed by hand.
 */

const norm = (value: string) => value.trim().toLowerCase()

/**
 * Whether a previous meter has anything to hand over. A row the last protocol
 * left blank (no number, no reading) is not offered: "übernehmen" on it would
 * only wipe what somebody has typed into the matching row since.
 */
export function meterOffersSomething(previous: PreviousMeter): boolean {
  return norm(previous.serial) !== '' || norm(previous.value) !== ''
}

/**
 * Whether a serial number identifies a meter. It does not when another meter
 * of the same protocol carries it — "ABCDEFG" on every row, "-" for "none",
 * the same test value typed four times. A number like that is a placeholder,
 * and matching on it sends every reading to the first row that happens to
 * hold it.
 */
function serialIdentifies(previous: PreviousMeter, siblings: readonly PreviousMeter[]): boolean {
  const serial = norm(previous.serial)
  if (!serial) return false
  return !siblings.some((other) => other.id !== previous.id && norm(other.serial) === serial)
}

/**
 * Which row a previous meter belongs to.
 *
 * By serial number first — a Zählernummer identifies a physical meter and
 * survives relabelling — but ONLY when it is one meter's number (see
 * `serialIdentifies`), and when several rows of this protocol carry it the
 * row of the same kind is taken. Then by kind and label, then by kind alone
 * when there is exactly one of that kind: the template's "Strom" row is the
 * one the last protocol's "Strom" reading was taken on, even if somebody
 * renamed it. A previous meter nothing matches is NEW to this protocol (an
 * extra water meter in the garden) and is offered as a row to add.
 *
 * `siblings` are the other meters of the same previous protocol; without them
 * the serial is trusted, which is right for a caller that has only one.
 */
export function matchMeter(
  previous: PreviousMeter,
  meters: MeterReading[],
  siblings: readonly PreviousMeter[] = [previous],
): MeterReading | null {
  if (serialIdentifies(previous, siblings)) {
    const serial = norm(previous.serial)
    const bySerial = meters.filter((meter) => norm(meter.serial) === serial)
    const sameKind = bySerial.filter((meter) => meter.kind === previous.kind)
    const found = (sameKind.length > 0 ? sameKind : bySerial)[0]
    if (found && (bySerial.length === 1 || sameKind.length > 0)) return found
  }

  const byLabel = meters.find(
    (meter) => meter.kind === previous.kind && norm(meter.label) === norm(previous.label),
  )
  if (byLabel) return byLabel

  if (previous.kind === 'other') return null
  const ofKind = meters.filter((meter) => meter.kind === previous.kind)
  return ofKind.length === 1 ? (ofKind[0] ?? null) : null
}

/** True when the row already holds exactly what the previous protocol had. */
export function meterTaken(
  previous: PreviousMeter,
  meters: MeterReading[],
  siblings: readonly PreviousMeter[] = [previous],
): boolean {
  const row = matchMeter(previous, meters, siblings)
  return (
    row !== null &&
    norm(row.serial) === norm(previous.serial) &&
    norm(row.value) === norm(previous.value)
  )
}

function meterRow(previous: PreviousMeter, id: string): MeterReading {
  return {
    id,
    kind: previous.kind,
    label: previous.label,
    serial: previous.serial,
    value: previous.value,
    unit: previous.unit,
    source: 'manual',
    note: '',
    photoIds: [],
  }
}

/** One meter, explicitly: overwrites the matched row, or adds one. */
export function takeMeter(
  meters: MeterReading[],
  previous: PreviousMeter,
  newId: () => string,
  siblings: readonly PreviousMeter[] = [previous],
): MeterReading[] {
  const row = matchMeter(previous, meters, siblings)
  if (!row) return [...meters, meterRow(previous, newId())]

  return meters.map((meter) =>
    meter.id === row.id
      ? {
          ...meter,
          serial: previous.serial,
          value: previous.value,
          unit: meter.unit || previous.unit,
        }
      : meter,
  )
}

/** Every meter, cautiously: fills blank serials and readings, adds the unknown. */
export function takeAllMeters(
  meters: MeterReading[],
  previous: PreviousMeter[],
  newId: () => string,
): MeterReading[] {
  let next = meters
  for (const entry of previous) {
    if (!meterOffersSomething(entry)) continue
    const row = matchMeter(entry, next, previous)
    if (!row) {
      next = [...next, meterRow(entry, newId())]
      continue
    }
    next = next.map((meter) =>
      meter.id === row.id
        ? {
            ...meter,
            serial: meter.serial.trim() ? meter.serial : entry.serial,
            value: meter.value.trim() ? meter.value : entry.value,
            unit: meter.unit || entry.unit,
          }
        : meter,
    )
  }
  return next
}

export function matchKey(previous: PreviousKey, keys: KeyItem[]): KeyItem | null {
  return keys.find((key) => norm(key.label) === norm(previous.label)) ?? null
}

export function keyTaken(previous: PreviousKey, keys: KeyItem[]): boolean {
  const row = matchKey(previous, keys)
  return row !== null && row.count === previous.count
}

/** One key count, explicitly: overwrites the matched row, or adds one. */
export function takeKey(keys: KeyItem[], previous: PreviousKey, newId: () => string): KeyItem[] {
  const row = matchKey(previous, keys)
  if (!row)
    return [...keys, { id: newId(), label: previous.label, count: previous.count, note: '' }]
  return keys.map((key) => (key.id === row.id ? { ...key, count: previous.count } : key))
}

/**
 * Every key, cautiously. "Blank" for a count is zero — the template starts
 * every key at 0, and a 0 somebody actually counted is indistinguishable from
 * one nobody touched. Taking ALL keys from the last protocol over a row that
 * says 0 is what the button is for; a non-zero count is left alone.
 */
export function takeAllKeys(
  keys: KeyItem[],
  previous: PreviousKey[],
  newId: () => string,
): KeyItem[] {
  let next = keys
  for (const entry of previous) {
    const row = matchKey(entry, next)
    if (!row) {
      next = [...next, { id: newId(), label: entry.label, count: entry.count, note: '' }]
      continue
    }
    if (row.count === 0) {
      next = next.map((key) => (key.id === row.id ? { ...key, count: entry.count } : key))
    }
  }
  return next
}
